import React, { useEffect, useMemo, useState, useContext, createContext } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  FiCheckCircle, FiClock, FiList, FiPhone, FiMail, FiGlobe,
  FiMapPin, FiUser, FiEye, FiTrash2,
  FiAlertCircle, FiRefreshCw,
} from "react-icons/fi";

const API       = "https://estatebackend.pixelmindsolutions.com";
const adminData = JSON.parse(sessionStorage.getItem("AdminData") || "{}");
const ADMIN_ID  = adminData?._id || adminData?.id || "";
const TABS      = { ALL: "all", APPROVED: "approved", PENDING: "pending" };

/* ─────────────────────────────────────────
   THEME CONTEXT  (same pattern as Dashboard)
───────────────────────────────────────── */
const ThemeCtx = createContext(false);
const useTheme = () => {
  const dm = useContext(ThemeCtx);
  const t  = (light, dark) => (dm ? dark : light);
  return { dm, t };
};

/* ─── Helpers ─── */
const getStatus = (p) => p.status || (p.isApproved ? "approved" : "pending");

const fmt = (price) =>
  price
    ? new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(price)
    : "—";

/* ─── Spinner ─── */
const Spin = ({ size = 14, cls = "" }) => (
  <span
    className={`inline-block rounded-full border-2 border-current border-t-transparent animate-spin ${cls}`}
    style={{ width: size, height: size, flexShrink: 0 }}
  />
);

/* ─── Status Badge ─── */
const StatusBadge = ({ p }) => {
  const { t } = useTheme();
  const s = getStatus(p);
  const cfg = {
    approved: { light: "bg-emerald-50 text-emerald-700 border-emerald-200", dark: "bg-emerald-900/30 text-emerald-400 border-emerald-800", icon: "✓" },
    pending:  { light: "bg-amber-50 text-amber-700 border-amber-200",       dark: "bg-amber-900/30 text-amber-400 border-amber-800",       icon: "⏳" },
    rejected: { light: "bg-red-50 text-red-700 border-red-200",             dark: "bg-red-900/30 text-red-400 border-red-800",             icon: "✗" },
  };
  const c = cfg[s] || cfg.pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-full border capitalize ${t(c.light, c.dark)}`}>
      {c.icon} {s}
    </span>
  );
};

/* ══════════ DELETE MODAL ══════════ */
const DeleteModal = ({ product, onClose, onConfirm, loading }) => {
  const { t } = useTheme();
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${t("bg-white", "bg-gray-900 border border-gray-700")}`}
        style={{ animation: "mslide 0.25s cubic-bezier(.22,.68,0,1.1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-red-500 to-red-600" />
        <div className="p-6 text-center">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 border
            ${t("bg-red-50 border-red-200 text-red-500", "bg-red-900/30 border-red-800 text-red-400")}`}>
            <FiTrash2 size={20} />
          </div>
          <h3 className={`text-lg font-bold mb-1.5 ${t("text-gray-900", "text-white")}`}>Delete Product?</h3>
          <p className={`text-sm ${t("text-gray-500", "text-gray-400")}`}>
            Permanently remove <span className={`font-semibold ${t("text-gray-800", "text-gray-200")}`}>{product.name}</span>.
            <br />This cannot be undone.
          </p>
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition
              ${t("bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100", "bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700")}`}>
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white
              bg-gradient-to-r from-red-500 to-red-600 shadow-lg shadow-red-900/30
              hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Spin size={13} /> : <FiTrash2 size={13} />}
            {loading ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ══════════ REJECT MODAL ══════════ */
const RejectModal = ({ product, onClose, onConfirm, loading }) => {
  const { t } = useTheme();
  const [reason, setReason] = useState("");
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-[9999] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className={`w-full max-w-sm rounded-2xl shadow-2xl overflow-hidden ${t("bg-white", "bg-gray-900 border border-gray-700")}`}
        style={{ animation: "mslide 0.25s cubic-bezier(.22,.68,0,1.1) both" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-1 bg-gradient-to-r from-orange-400 to-orange-600" />
        <div className="p-6 text-center">
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 border
            ${t("bg-orange-50 border-orange-200 text-orange-500", "bg-orange-900/30 border-orange-800 text-orange-400")}`}>
            <FiAlertCircle size={20} />
          </div>
          <h3 className={`text-lg font-bold mb-1 ${t("text-gray-900", "text-white")}`}>Reject Listing?</h3>
          <p className={`text-sm mb-4 ${t("text-gray-500", "text-gray-400")}`}>
            Rejecting <span className={`font-semibold ${t("text-gray-800", "text-gray-200")}`}>{product.name}</span>. Provide a reason below.
          </p>
          <textarea
            rows={3}
            placeholder="Enter rejection reason…"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            className={`w-full px-3 py-2.5 rounded-xl border text-sm resize-none outline-none transition text-left
              ${t(
                "bg-gray-50 border-gray-200 text-gray-800 placeholder-gray-400 focus:border-orange-400 focus:ring-2 focus:ring-orange-100",
                "bg-gray-800 border-gray-600 text-white placeholder-gray-500 focus:border-orange-500 focus:ring-2 focus:ring-orange-900/40"
              )}`}
          />
        </div>
        <div className="flex gap-3 px-6 pb-6">
          <button onClick={onClose}
            className={`flex-1 py-2.5 rounded-xl text-sm font-semibold border transition
              ${t("bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100", "bg-gray-800 border-gray-600 text-gray-300 hover:bg-gray-700")}`}>
            Cancel
          </button>
          <button onClick={() => onConfirm(reason)} disabled={loading || !reason.trim()}
            className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-white
              bg-gradient-to-r from-orange-400 to-orange-600 shadow-lg shadow-orange-900/20
              hover:opacity-90 transition disabled:opacity-50 flex items-center justify-center gap-2">
            {loading ? <Spin size={13} /> : <FiAlertCircle size={13} />}
            {loading ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/* ══════════════════════════════════════
   MOBILE PRODUCT CARD  (shown on < md)
══════════════════════════════════════ */
const MobileCard = ({ p, i, showActions, onApprove, onReject, onDelete, onView, actionLoading }) => {
  const { t } = useTheme();
  const status = getStatus(p);
  return (
    <div className={`rounded-2xl border p-4 transition ${t("bg-white border-gray-100 shadow-sm", "bg-gray-800/70 border-gray-700/60")}`}>
      {/* Top: thumb + name + status */}
      <div className="flex items-start gap-3 mb-3">
        <div className="relative flex-shrink-0">
          <img
            src={p.images?.[0]}
            alt={p.name}
            onError={(e) => { e.target.src = "https://placehold.co/48"; }}
            className={`w-12 h-12 rounded-xl object-cover border ${t("border-gray-100", "border-gray-700")}`}
          />
          {p.images?.length > 1 && (
            <span className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white
              text-[9px] font-bold flex items-center justify-center border-2 border-white">
              +{p.images.length - 1}
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className={`text-sm font-bold truncate ${t("text-gray-900", "text-white")}`}>{p.name}</p>
          {p.description && (
            <p className={`text-xs truncate mt-0.5 ${t("text-gray-400", "text-gray-500")}`}>{p.description}</p>
          )}
          <div className="mt-1.5"><StatusBadge p={p} /></div>
        </div>
        <span className={`text-[10px] font-mono flex-shrink-0 ${t("text-gray-300", "text-gray-600")}`}>#{i + 1}</span>
      </div>

      {/* Info grid */}
      <div className={`grid grid-cols-2 gap-x-4 gap-y-2.5 text-xs pt-3 mb-3 border-t ${t("border-gray-100", "border-gray-700")}`}>
        {p.category?.name && (
          <div>
            <span className={`block text-[10px] font-bold uppercase tracking-wider mb-0.5 ${t("text-gray-400", "text-gray-500")}`}>Category</span>
            <span className={`font-medium ${t("text-gray-700", "text-gray-300")}`}>{p.category.name}</span>
          </div>
        )}
        {p.user?.name && (
          <div>
            <span className={`block text-[10px] font-bold uppercase tracking-wider mb-0.5 ${t("text-gray-400", "text-gray-500")}`}>Owner</span>
            <span className={`font-medium ${t("text-gray-700", "text-gray-300")}`}>{p.user.name}</span>
          </div>
        )}
        {p.attributes?.price && (
          <div>
            <span className={`block text-[10px] font-bold uppercase tracking-wider mb-0.5 ${t("text-gray-400", "text-gray-500")}`}>Price</span>
            <span className="font-bold text-emerald-600">{fmt(p.attributes.price)}</span>
          </div>
        )}
        {p.address && (
          <div className="col-span-2">
            <span className={`block text-[10px] font-bold uppercase tracking-wider mb-0.5 ${t("text-gray-400", "text-gray-500")}`}>Address</span>
            <div className="flex items-start gap-1">
              <FiMapPin size={10} className="text-red-400 flex-shrink-0 mt-0.5" />
              <span className={t("text-gray-500", "text-gray-400")}>{p.address}</span>
            </div>
          </div>
        )}
        {(p.contact?.callNumber || p.contact?.email || p.contact?.website) && (
          <div className="col-span-2">
            <span className={`block text-[10px] font-bold uppercase tracking-wider mb-1 ${t("text-gray-400", "text-gray-500")}`}>Contact</span>
            <div className="flex flex-wrap gap-3">
              {p.contact?.callNumber && (
                <a href={`tel:${p.contact.callNumber}`} className="flex items-center gap-1 text-blue-500 hover:underline">
                  <FiPhone size={10} /> {p.contact.callNumber}
                </a>
              )}
              {p.contact?.email && (
                <a href={`mailto:${p.contact.email}`} className="flex items-center gap-1 text-violet-500 hover:underline truncate max-w-[180px]">
                  <FiMail size={10} /> {p.contact.email}
                </a>
              )}
              {p.contact?.website && (
                <a
                  href={p.contact.website.startsWith("http") ? p.contact.website : `https://${p.contact.website}`}
                  target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-1 text-cyan-500 hover:underline truncate max-w-[180px]">
                  <FiGlobe size={10} /> {p.contact.website}
                </a>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={`flex items-center gap-2 pt-3 border-t flex-wrap ${t("border-gray-100", "border-gray-700")}`}>
        {showActions && status === "pending" && (
          <>
            <button
              onClick={() => onApprove(p._id)}
              disabled={actionLoading === p._id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold
                bg-emerald-50 text-emerald-700 border border-emerald-200
                hover:bg-emerald-100 disabled:opacity-50 transition"
            >
              {actionLoading === p._id ? <Spin size={10} cls="text-emerald-600" /> : "✓"} Approve
            </button>
            <button
              onClick={() => onReject(p)}
              disabled={actionLoading === p._id}
              className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold
                bg-amber-50 text-amber-700 border border-amber-200
                hover:bg-amber-100 disabled:opacity-50 transition"
            >
              ✗ Reject
            </button>
          </>
        )}
        <div className="flex items-center gap-2 ml-auto">
          <button onClick={() => onView(p._id)} title="View"
            className="w-8 h-8 rounded-lg border flex items-center justify-center
              bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100 transition">
            <FiEye size={13} />
          </button>
          <button onClick={() => onDelete(p)} title="Delete"
            className="w-8 h-8 rounded-lg border flex items-center justify-center
              bg-red-50 border-red-200 text-red-500 hover:bg-red-100 transition">
            <FiTrash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
const AllHouses = ({ darkMode = false }) => {
  const dm = !!darkMode;
  const t  = (light, dark) => (dm ? dark : light);

  const [activeTab,     setActiveTab]     = useState(TABS.ALL);
  const [products,        setProducts]        = useState([]);
  const [pendingProducts, setPendingProducts] = useState([]);
  const [approvedProducts, setApprovedProducts] = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [rejectProduct, setRejectProduct] = useState(null);

  const navigate = useNavigate();

  /* Fetch all products (for All & Approved tabs) */
  const fetchAll = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/all`);
      setProducts(res.data.products || []);
    } catch (err) {
      console.error("Failed to load products:", err);
    } finally {
      setLoading(false);
    }
  };

  /* Fetch pending products from dedicated endpoint */
  const fetchPending = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/pending`);
      setPendingProducts(res.data.products || []);
    } catch (err) {
      console.error("Failed to load pending products:", err);
    } finally {
      setLoading(false);
    }
  };

  /* Fetch approved products from dedicated endpoint */
  const fetchApproved = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/api/admin/approved`);
      setApprovedProducts(res.data.products || []);
    } catch (err) {
      console.error("Failed to load approved products:", err);
    } finally {
      setLoading(false);
    }
  };

  /* On mount: load all three sources so counts are accurate immediately */
  useEffect(() => {
    fetchAll();
    fetchPending();
    fetchApproved();
  }, []);

  /* When tab changes, refresh the relevant data */
  const handleTabChange = (key) => {
    setActiveTab(key);
    if (key === TABS.PENDING)  fetchPending();
    else if (key === TABS.APPROVED) fetchApproved();
    else fetchAll();
  };

  /* Use dedicated approvedProducts for Approved tab; derive from products for All tab */
  const approvedFromAll = useMemo(() => products.filter((p) => getStatus(p) === "approved"), [products]);

  /* Use dedicated pendingProducts for Pending tab; derive from products for counts on other tabs */
  const pendingFromAll = useMemo(() => products.filter((p) => getStatus(p) === "pending"), [products]);

  const displayed = useMemo(() => {
    if (activeTab === TABS.APPROVED) return approvedProducts;
    if (activeTab === TABS.PENDING)  return pendingProducts;
    return products;
  }, [activeTab, products, approvedProducts, pendingProducts]);

  const counts = {
    all:      products.length,
    approved: approvedProducts.length || approvedFromAll.length,
    pending:  pendingProducts.length  || pendingFromAll.length,
  };

  /* Refresh helper — refreshes the correct data source based on active tab, keeps all counts in sync */
  const handleRefresh = () => {
    if (activeTab === TABS.PENDING)       fetchPending();
    else if (activeTab === TABS.APPROVED) fetchApproved();
    else fetchAll();
    /* Always keep badge counts fresh */
    fetchPending();
    fetchApproved();
  };

  const approveProduct = async (productId) => {
    try {
      setActionLoading(productId);
      await axios.post(`${API}/api/admin/approve`, { productId, adminId: ADMIN_ID, status: "approved" });
      /* Refresh all three so counts stay accurate everywhere */
      await Promise.all([fetchAll(), fetchPending(), fetchApproved()]);
    } finally { setActionLoading(null); }
  };

  const confirmReject = async (reason) => {
    if (!rejectProduct) return;
    try {
      setRejectLoading(true);
      await axios.post(`${API}/api/admin/reject`, {
        productId: rejectProduct._id, adminId: ADMIN_ID,
        status: "rejected", rejectionReason: reason,
      });
      setRejectProduct(null);
      await Promise.all([fetchAll(), fetchPending(), fetchApproved()]);
    } finally { setRejectLoading(false); }
  };

  const confirmDelete = async () => {
    if (!deleteProduct) return;
    try {
      setDeleteLoading(true);
      await axios.delete(`${API}/api/product/${deleteProduct._id}`);
      setDeleteProduct(null);
      await Promise.all([fetchAll(), fetchPending(), fetchApproved()]);
    } finally { setDeleteLoading(false); }
  };

  const showApproveReject = activeTab === TABS.PENDING;

  const tabDefs = [
    { key: TABS.ALL,      icon: <FiList size={13} />,        label: "All Products" },
    { key: TABS.APPROVED, icon: <FiCheckCircle size={13} />, label: "Approved"     },
    { key: TABS.PENDING,  icon: <FiClock size={13} />,       label: "Pending"      },
  ];

  /* Desktop table columns */
  const cols = ["#", "Product", "Category", "Address", "Contact", "Status",
    ...(showApproveReject ? ["Approve / Reject"] : []), "Actions"];

  return (
    <ThemeCtx.Provider value={dm}>
      <>
        <style>{`
          @keyframes mslide {
            from { opacity:0; transform:translateY(14px) scale(0.97); }
            to   { opacity:1; transform:translateY(0) scale(1); }
          }
          @keyframes fadeUp {
            from { opacity:0; transform:translateY(12px); }
            to   { opacity:1; transform:translateY(0); }
          }
          .ah-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
          .ah-scroll::-webkit-scrollbar-track { background: transparent; }
          .ah-scroll::-webkit-scrollbar-thumb { background: #fca5a5; border-radius: 99px; }
          .ah-scroll::-webkit-scrollbar-thumb:hover { background: #f87171; }
        `}</style>

        <div className={`min-h-screen p-3 sm:p-5 lg:p-7 transition-colors duration-300
          ${t(
            "bg-gradient-to-br from-red-50/50 via-white to-rose-50/30",
            "bg-gray-950"
          )}`}>

          <div
            className={`max-w-[1400px] mx-auto rounded-2xl border overflow-hidden shadow-sm
              ${t("bg-white border-gray-100", "bg-gray-900 border-gray-700/60")}`}
            style={{ animation: "fadeUp 0.4s ease both" }}
          >
            {/* Red top strip */}
            <div className="h-1 bg-gradient-to-r from-red-500 to-red-600" />

            {/* ══ HEADER ══ */}
            <div className={`flex flex-wrap items-end justify-between gap-4 px-5 sm:px-7 py-5 border-b
              ${t("border-gray-100", "border-gray-700/60")}`}>
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.18em] mb-1
                  bg-gradient-to-r from-red-500 to-red-600 bg-clip-text text-transparent">
                  Admin · Listings
                </p>
                <h1 className={`text-2xl sm:text-3xl font-extrabold tracking-tight leading-none
                  ${t("text-gray-900", "text-white")}`}>
                  Product <span className="text-red-500">Management</span>
                </h1>
                {!loading && (
                  <p className={`text-xs mt-1.5 ${t("text-gray-400", "text-gray-500")}`}>
                    {counts.approved} approved · {counts.pending} pending · {counts.all} total
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                {!loading && (
                  <>
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border
                      ${t("bg-emerald-50 border-emerald-200 text-emerald-700", "bg-emerald-900/30 border-emerald-800 text-emerald-400")}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {counts.approved} Approved
                    </span>
                    <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border
                      ${t("bg-amber-50 border-amber-200 text-amber-700", "bg-amber-900/30 border-amber-800 text-amber-400")}`}>
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                      {counts.pending} Pending
                    </span>
                  </>
                )}
                <button onClick={handleRefresh}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition
                    ${t(
                      "bg-red-50 border-red-200 text-red-600 hover:bg-red-100",
                      "bg-red-900/30 border-red-800 text-red-400 hover:bg-red-900/50"
                    )}`}>
                  <FiRefreshCw size={12} /> Refresh
                </button>
              </div>
            </div>

            {/* ══ TABS ══ */}
            <div className={`flex px-5 sm:px-7 border-b overflow-x-auto
              ${t("border-gray-100", "border-gray-700/60")}`}
              style={{ scrollbarWidth: "none" }}>
              {tabDefs.map(({ key, icon, label }) => (
                <button key={key} onClick={() => handleTabChange(key)}
                  className={`flex items-center gap-2 px-4 py-3.5 border-b-2 text-xs font-semibold
                    whitespace-nowrap transition-all duration-150 flex-shrink-0
                    ${activeTab === key
                      ? "border-red-500 text-red-500"
                      : `border-transparent ${t("text-gray-400 hover:text-red-500", "text-gray-500 hover:text-red-400")}`
                    }`}>
                  {icon}
                  {label}
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5
                    rounded-full text-[10px] font-bold
                    ${activeTab === key
                      ? t("bg-red-50 border border-red-200 text-red-600", "bg-red-900/30 border border-red-800 text-red-400")
                      : t("bg-gray-100 text-gray-400", "bg-gray-800 text-gray-500")
                    }`}>
                    {counts[key]}
                  </span>
                </button>
              ))}
            </div>

            {/* ══ LOADING ══ */}
            {loading && (
              <div className="py-20 flex flex-col items-center gap-3">
                <div className={`w-10 h-10 rounded-full border-[3px] border-t-red-500 animate-spin
                  ${t("border-red-100", "border-red-900")}`} />
                <p className={`text-sm font-medium ${t("text-gray-400", "text-gray-500")}`}>Loading products…</p>
              </div>
            )}

            {/* ══ EMPTY ══ */}
            {!loading && displayed.length === 0 && (
              <div className="py-20 flex flex-col items-center gap-3">
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border
                  ${t("bg-red-50 border-red-200 text-red-500", "bg-red-900/30 border-red-800 text-red-400")}`}>
                  <FiAlertCircle size={22} />
                </div>
                <p className={`text-base font-bold ${t("text-gray-800", "text-white")}`}>No products found</p>
                <p className={`text-sm ${t("text-gray-400", "text-gray-500")}`}>Nothing to display in this tab</p>
              </div>
            )}

            {/* ══ MOBILE CARDS (< md) ══ */}
            {!loading && displayed.length > 0 && (
              <div
                className="block md:hidden overflow-y-auto ah-scroll"
                style={{ maxHeight: "calc(100vh - 280px)" }}
              >
                <div className="p-4 space-y-3">
                  {displayed.map((p, i) => (
                    <MobileCard
                      key={p._id} p={p} i={i}
                      showActions={showApproveReject}
                      onApprove={approveProduct}
                      onReject={setRejectProduct}
                      onDelete={setDeleteProduct}
                      onView={(id) => navigate(`/admin/products/${id}`)}
                      actionLoading={actionLoading}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* ══ DESKTOP TABLE (≥ md) ══ */}
            {!loading && displayed.length > 0 && (
              <div
                className="hidden md:block overflow-auto ah-scroll"
                style={{ maxHeight: "calc(100vh - 280px)" }}
              >
                <table className="w-full text-sm border-collapse min-w-[900px]">
                  <thead className="sticky top-0 z-10">
                    <tr className={`border-b ${t("bg-gray-50/80 border-gray-100", "bg-gray-800/50 border-gray-700")}`}>
                      {cols.map((h) => (
                        <th key={h}
                          className={`px-4 py-3 text-left text-[10px] font-bold uppercase tracking-widest whitespace-nowrap
                            ${t("text-gray-400", "text-gray-500")}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.map((p, i) => (
                      <tr key={p._id}
                        className={`border-b transition-colors last:border-0
                          ${t("border-gray-50 hover:bg-red-50/40", "border-gray-700/40 hover:bg-red-900/10")}`}>

                        {/* # */}
                        <td className="px-4 py-3.5">
                          <span className={`text-[11px] font-mono ${t("text-gray-300", "text-gray-600")}`}>{i + 1}</span>
                        </td>

                        {/* Product */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-3 min-w-[160px]">
                            <div className="relative flex-shrink-0">
                              <img
                                src={p.images?.[0]} alt={p.name}
                                onError={(e) => { e.target.src = "https://placehold.co/44"; }}
                                className={`w-11 h-11 rounded-xl object-cover border ${t("border-gray-100", "border-gray-700")}`}
                              />
                              {p.images?.length > 1 && (
                                <span className="absolute -bottom-1 -right-1 w-[18px] h-[18px] rounded-full
                                  bg-red-500 text-white text-[9px] font-bold flex items-center justify-center
                                  border-2 border-white">
                                  +{p.images.length - 1}
                                </span>
                              )}
                            </div>
                            <div className="max-w-[150px]">
                              <p className={`text-sm font-semibold leading-tight truncate ${t("text-gray-900", "text-white")}`}>{p.name}</p>
                              {p.description && (
                                <p className={`text-xs truncate mt-0.5 ${t("text-gray-400", "text-gray-500")}`}>{p.description}</p>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Category */}
                        <td className="px-4 py-3.5">
                          <span className={`text-xs font-semibold px-2.5 py-1 rounded-full
                            ${t("bg-gray-100 text-gray-500", "bg-gray-800 text-gray-400")}`}>
                            {p.category?.name || "—"}
                          </span>
                        </td>

                        {/* Address */}
                        <td className="px-4 py-3.5 max-w-[150px]">
                          <div className="flex items-start gap-1">
                            <FiMapPin size={11} className="text-red-400 flex-shrink-0 mt-0.5" />
                            <span className={`text-xs leading-snug ${t("text-gray-500", "text-gray-400")}`}>{p.address || "—"}</span>
                          </div>
                        </td>

                        {/* Contact */}
                        <td className="px-4 py-3.5">
                          <div className="flex flex-col gap-1 min-w-[120px]">
                            {p.contact?.callNumber && (
                              <a href={`tel:${p.contact.callNumber}`} className="flex items-center gap-1 text-[11px] text-blue-500 hover:underline whitespace-nowrap">
                                <FiPhone size={10} /> {p.contact.callNumber}
                              </a>
                            )}
                            {p.contact?.email && (
                              <a href={`mailto:${p.contact.email}`} className="flex items-center gap-1 text-[11px] text-violet-500 hover:underline truncate max-w-[140px]">
                                <FiMail size={10} /> {p.contact.email}
                              </a>
                            )}
                            {p.contact?.website && (
                              <a
                                href={p.contact.website.startsWith("http") ? p.contact.website : `https://${p.contact.website}`}
                                target="_blank" rel="noopener noreferrer"
                                className="flex items-center gap-1 text-[11px] text-cyan-500 hover:underline truncate max-w-[140px]">
                                <FiGlobe size={10} /> {p.contact.website}
                              </a>
                            )}
                          </div>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3.5"><StatusBadge p={p} /></td>

                        {/* Approve / Reject */}
                        {showApproveReject && (
                          <td className="px-4 py-3.5">
                            {getStatus(p) === "pending" ? (
                              <div className="flex flex-col gap-1.5 min-w-[90px]">
                                <button
                                  onClick={() => approveProduct(p._id)}
                                  disabled={actionLoading === p._id}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold
                                    bg-emerald-50 text-emerald-700 border border-emerald-200
                                    hover:bg-emerald-100 disabled:opacity-50 transition">
                                  {actionLoading === p._id ? <Spin size={10} cls="text-emerald-600" /> : "✓"} Approve
                                </button>
                                <button
                                  onClick={() => setRejectProduct(p)}
                                  disabled={actionLoading === p._id}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold
                                    bg-amber-50 text-amber-700 border border-amber-200
                                    hover:bg-amber-100 disabled:opacity-50 transition">
                                  ✗ Reject
                                </button>
                              </div>
                            ) : (
                              <span className={`text-xs ${t("text-gray-300", "text-gray-600")}`}>—</span>
                            )}
                          </td>
                        )}

                        {/* Actions */}
                        <td className="px-4 py-3.5">
                          <div className="flex items-center gap-1.5">
                            <button title="View" onClick={() => navigate(`/admin/products/${p._id}`)}
                              className="w-8 h-8 rounded-lg border flex items-center justify-center transition
                                bg-blue-50 border-blue-200 text-blue-600 hover:bg-blue-100">
                              <FiEye size={13} />
                            </button>
                            <button title="Delete" onClick={() => setDeleteProduct(p)}
                              className="w-8 h-8 rounded-lg border flex items-center justify-center transition
                                bg-red-50 border-red-200 text-red-500 hover:bg-red-100">
                              <FiTrash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* ══ FOOTER ══ */}
            {!loading && (
              <div className={`px-5 sm:px-7 py-3 border-t text-right text-xs
                ${t("border-gray-100 text-gray-400", "border-gray-700 text-gray-500")}`}>
                Showing {displayed.length} product{displayed.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
        </div>

        {deleteProduct && (
          <DeleteModal
            product={deleteProduct}
            onClose={() => setDeleteProduct(null)}
            onConfirm={confirmDelete}
            loading={deleteLoading}
          />
        )}
        {rejectProduct && (
          <RejectModal
            product={rejectProduct}
            onClose={() => setRejectProduct(null)}
            onConfirm={confirmReject}
            loading={rejectLoading}
          />
        )}
      </>
    </ThemeCtx.Provider>
  );
};

export default AllHouses;